/**
 * Модальное окно на нативном `<dialog>`: `showModal()` бесплатно даёт верхний
 * слой, ловушку фокуса, Esc и подложку — без npm-библиотеки диалогов. Им
 * открываются формы «+»: создание — редкое действие и не должно висеть на
 * странице постоянно.
 *
 * Закрывается крестиком, Esc и кликом по подложке; все три пути идут в
 * `onClose`, состояние «открыт» живёт у вызывающего экрана.
 *
 * `wide` — окно выбора по картинкам (06.10.26): почти во весь экран, прокручивается
 * само окно, шапка прилипает. Прокрутку окна ленивые превью (`RetryImg`) берут
 * корнем наблюдения — ближайший `dialog`, а не `main`.
 *
 * `locked` — закрывается только крестиком (окна с правкой, №376: выделение текста, отпущенное над
 * подложкой, и Esc закрывали окно и теряли несохранённое).
 */
import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { btnGhost } from "./classes";

export function Dialog({
  open,
  title,
  onClose,
  children,
  wide = false,
  locked = false,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  locked?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      // `close` в DOM не всплывает, но React 19 отдаёт его всем `onClose` по дереву компонентов:
      // закрытие вложенного окна (выбор платья в «Загрузить») закрывало и внешнее (08.10.26).
      onClose={(e) => e.target === e.currentTarget && onClose()}
      // Клик мимо содержимого попадает в сам <dialog> — это подложка.
      onClick={(e) => !locked && e.target === e.currentTarget && onClose()}
      // Esc шлёт `cancel`, а за ним `close`: у запертого окна гасим сам `cancel`
      onCancel={(e) => locked && e.preventDefault()}
      className={`m-auto ${
        wide ? "h-[calc(100vh-2rem)] w-[1240px] overflow-y-auto" : "w-[460px]"
      } max-w-[calc(100vw-2rem)] rounded-[var(--radius-card)] border border-border bg-bg p-0 text-fg shadow-xl backdrop:bg-black/50`}
    >
      {open ? (
        <div className={wide ? "flex min-h-full flex-col" : undefined}>
          <div
            className={`flex items-center gap-2 border-b border-border px-4 py-2.5 ${wide ? "sticky top-0 z-20 bg-bg" : ""}`}
          >
            <h2 className="text-[14px] font-medium text-fg">{title}</h2>
            <button type="button" className={`${btnGhost} ml-auto`} onClick={onClose} aria-label="Закрыть">
              <X size={14} />
            </button>
          </div>
          {children}
        </div>
      ) : null}
    </dialog>
  );
}
